"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomerAuthn = void 0;
const tslib_1 = require("tslib");
const MessageRouterImp_1 = require("./message/MessageRouterImp");
const JoinInteractionHandler_1 = require("./joinInteraction/JoinInteractionHandler");
const SocketClient_1 = require("./comms/SocketClient");
const Config_1 = require("./Config");
const SetAuthenticationFactorHandler_1 = require("./setAuthenticationFactor/SetAuthenticationFactorHandler");
const InteractionCompletedHandler_1 = require("./interactionCompleted/InteractionCompletedHandler");
const JoinService_1 = require("./joinInteraction/JoinService");
const ChallengeCustomerHandler_1 = require("./challengeCustomer/ChallengeCustomerHandler");
const CustomerChallengedHandler_1 = require("./challengeCustomer/CustomerChallengedHandler");
const AuthenticationFactorSetHandler_1 = require("./setAuthenticationFactor/AuthenticationFactorSetHandler");
const EngageEndpointHandler_1 = require("./engageEndpoint/EngageEndpointHandler");
const EndpointEngagedHandler_1 = require("./engageEndpoint/EndpointEngagedHandler");
const Webauthn_1 = require("./webauthn/Webauthn");
const OnFidoImplementation_1 = require("./identifyCustomer/OnFidoImplementation");
/**
 * The main API for interacting with CustomerAuthn.
 */
class CustomerAuthn {
    constructor(socketClient, router, joinService, webauthn, channelID, identifyCustomerProvider) {
        this.socketClient = socketClient;
        this.router = router;
        this.joinService = joinService;
        this.webauthn = webauthn;
        this.channelID = channelID;
        this.identifyCustomerProvider = identifyCustomerProvider;
        this.onCustomerChallengedHandler = new CustomerChallengedHandler_1.CustomerChallengedHandler(this.router, this.socketClient);
        this.onAuthenticationFactorSetHandler = new AuthenticationFactorSetHandler_1.AuthenticationFactorSetHandler(this.router, this.socketClient);
        this.onChallengeCustomerHandler = new ChallengeCustomerHandler_1.ChallengeCustomerHandler(this.router, this.joinService, this.onCustomerChallengedHandler, this.socketClient, this.webauthn);
        this.onSetAuthenticationFactorHandler = new SetAuthenticationFactorHandler_1.SetAuthenticationFactorHandler(this.router, this.joinService, this.onAuthenticationFactorSetHandler, this.socketClient, this.webauthn);
        this.onInteractionCompletedHandler = new InteractionCompletedHandler_1.InteractionCompletedHandler(this.router, this.socketClient);
        this.onEndpointEngagedHandler = new EndpointEngagedHandler_1.EndpointEngagedHandler(this.router, this.socketClient);
        this.onEngageEndpointHandler = new EngageEndpointHandler_1.EngageEndpointHandler(this.router, this.onEndpointEngagedHandler, this.socketClient, this.identifyCustomerProvider);
    }
    /**
     * Used to indicate that an existing interaction needs to be joined. This is used when the interaction originated on a system other than the browser.
     *
     * @param {IJoinOptions} joinOptions The request to join an interaction
     * @return {Promise<void>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelID: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * await sdk.joinInteraction({ entersektJoinCode: "fec5dc04-cd76-4ba6-b05e-977f1f339beb" });
     * ```
     */
    joinInteraction(joinOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const joinInteractionHandler = new JoinInteractionHandler_1.JoinInteractionHandler(this.router, this.socketClient, this.joinService, this.channelID);
            yield joinInteractionHandler.joinInteraction(joinOptions);
        });
    }
    /**
     * Observable to monitor for events when a new notify event is received
     * @returns {Subject<INotify>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelID: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onNotify.subscribe().then(async (notify) => {
     *  if (notify.userInteractionRequired){
     *    await notify.proceed();
     *  }
     * });
     * ```
     */
    onNotify() {
        return this.onEngageEndpointHandler.onNotify();
    }
    /**
     * Observable to monitor for events when a new redirect event is received
     * @returns {Subject<IRedirect>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelID: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onRedirect.subscribe().then(async (redirect) => {
     *  if (redirect.userInteractionRequired){
     *    await redirect.proceed();
     *  }
     * });
     * ```
     */
    onRedirect() {
        return this.onEngageEndpointHandler.onRedirect();
    }
    /**
     * Observable to monitor for events when a new identify customer event is received
     * @returns {Subject<IIdentifyCustomer>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelID: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onIdentifyCustomer.subscribe().then(async (identifyCustomer) => {
     *   // show identifyCustomer UI
     *   await identifyCustomer.proceed()
     *   // hide identifyCustomer UI
     * });
     * ```
     */
    onIdentifyCustomer() {
        return this.onEngageEndpointHandler.onIdentifyCustomer();
    }
    /**
     * Observable to monitor for events when a new challenge is received
     * @returns {Subject<IChallenge>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelID: "SOME_CHANNEL"
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onChallenge.subscribe().then(async (challenge) => {
     *  await challenge.proceed();
     * });
     * ```
     */
    onChallengeCustomer() {
        return this.onChallengeCustomerHandler.onChallengeCustomer();
    }
    /**
     * Observable to monitor for events when a new authentication factor is being registered
     *
     * @returns {Subject<ISetAuthenticationFactor>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelShortName: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onSetAuthenticationFactor.subscribe().then(async (result) => {
     *  await result.proceed();
     * });
     * ```
     */
    onSetAuthenticationFactor() {
        return this.onSetAuthenticationFactorHandler.onSetAuthenticationFactor();
    }
    /**
     * Observable to monitor for events when an interaction has been completed
     *
     * @returns {Subject<IInteractionCompleted>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelShortName: "SOME_CHANNEL",
     * };
     * const sdk = await CustomerAuthn.init(config);
     * sdk.onInteractionCompleted.subscribe().then(async (result) => {
     *  console.log(result)
     * });
     * ```
     */
    onInteractionCompleted() {
        return this.onInteractionCompletedHandler.onInteractionCompleted();
    }
    /**
     * Used to initialize the SDK. This method should be called prior to making any calls to the SDK.
     * @param {ICustomerAuthnConfig} configParams Configuration object containing details such as `connectionURL`
     * @returns {Promise<CustomerAuthn>}
     * @example
     * ```
     * import CustomerAuthn from "@entersekt/customerauthn-sdk-web"
     *
     * const config: ICustomerAuthnConfig = {
     *  connectionURL: "wss://connection.example.com/live",
     *  timeoutInMilliseconds: 10000,
     *  channelShortName: "SOME_CHANNEL",
     * };
     * CustomerAuthn.init(config).then(() => {
     *  // success
     * }).catch((error) => {
     *  // error
     * });
     * ```
     */
    static init(configParams) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const handler = new MessageRouterImp_1.default();
            const socketClient = new SocketClient_1.default(handler, (configParams === null || configParams === void 0 ? void 0 : configParams.connectionURL) || Config_1.config.connectionURL);
            const joinService = new JoinService_1.JoinService();
            const webauthn = new Webauthn_1.Webauthn();
            const onFido = new OnFidoImplementation_1.OnFidoImplementation();
            yield socketClient.connect();
            return new CustomerAuthn(socketClient, handler, joinService, webauthn, configParams.channelID, onFido);
        });
    }
}
exports.CustomerAuthn = CustomerAuthn;
//# sourceMappingURL=CustomerAuthn.js.map